import React from 'react';
import {
    Box,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    FormHelperText,
    Checkbox,
    Grid,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import BootstrapDialog, {
    BootstrapDialogTitle,
} from '../common/dialog/BootstrapDialog';
import * as invApi from '../../lib/api/inventory';

const WarehouseFormModal = ({ open, handleClose, data }) => {
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = !!data;

    const validationSchema = yup.object({
        whNm: yup.string().required('창고명을 입력하세요'),
        whDesc: yup.string().nullable(),
        whAddr: yup.string().nullable(),
        whCate1: yup.string().nullable(),
        whCate2: yup.string().nullable(),
        isDefault: yup.boolean(),
        shipYn: yup.boolean(),
    });

    const formik = useFormik({
        initialValues: {
            whNm: data?.whNm || '',
            whDesc: data?.whDesc || '',
            whAddr: data?.whAddr || '',
            whCate1: data?.whCate1 || '',
            whCate2: data?.whCate2 || '',
            isDefault: data?.isDefault === 'Y' || false,
            shipYn: data?.shipYn === 'Y' || false,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
                const submitData = {
                    ...values,
                    isDefault: values.isDefault ? 'Y' : 'N',
                    shipYn: values.shipYn ? 'Y' : 'N',
                };

                if (isEdit) {
                    await invApi.editWarehouse(data.whUuid, submitData);
                    enqueueSnackbar('창고가 수정되었습니다', { variant: 'success' });
                } else {
                    await invApi.addWarehouse(submitData);
                    enqueueSnackbar('창고가 등록되었습니다', { variant: 'success' });
                }
                handleClose(true);
            } catch (err) {
                const message =
                    typeof err.response !== 'undefined'
                        ? err.response.data.message
                        : err.message;
                enqueueSnackbar(message, { variant: 'error' });
            }
            setSubmitting(false);
        },
    });

    return (
        <BootstrapDialog
            onClose={() => handleClose(false)}
            aria-labelledby="dialog-title"
            open={open}
            fullWidth
            maxWidth="sm"
        >
            <BootstrapDialogTitle
                id="dialog-title"
                onClose={() => handleClose(false)}
            >
                {isEdit ? '창고 수정' : '창고 추가'}
            </BootstrapDialogTitle>
            <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="창고명"
                                name="whNm"
                                value={formik.values.whNm}
                                onChange={formik.handleChange}
                                error={formik.touched.whNm && Boolean(formik.errors.whNm)}
                                helperText={formik.touched.whNm && formik.errors.whNm}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="주소"
                                name="whAddr"
                                value={formik.values.whAddr}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="설명"
                                name="whDesc"
                                value={formik.values.whDesc}
                                onChange={formik.handleChange}
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="분류1 (지역)"
                                name="whCate1"
                                value={formik.values.whCate1}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="분류2 (유형)"
                                name="whCate2"
                                value={formik.values.whCate2}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formik.values.isDefault}
                                        onChange={formik.handleChange}
                                        name="isDefault"
                                    />
                                }
                                label="기본 창고로 설정"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formik.values.shipYn}
                                        onChange={formik.handleChange}
                                        name="shipYn"
                                    />
                                }
                                label="출고용 창고"
                            />
                            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                                여기 있는 물건만 출고 요청 대상이 됩니다. 자재 보관용 창고는 꺼두세요.
                            </FormHelperText>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose(false)}>취소</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting}
                    >
                        {isEdit ? '수정' : '등록'}
                    </Button>
                </DialogActions>
            </Box>
        </BootstrapDialog>
    );
};

export default WarehouseFormModal;
